<template>
  <BaseForm
    :key="ticketId || 'new'"
    :title="formTitle"
    :formData="ticketHaulData"
    :fields="fields"
    :submitUrl="submitUrl"
    :method="method"
    :selectedYear="selectedYear"
    :dateRestriction="restrictDates"
    ref="baseForm"
    @form-success="handleSuccess"
    @form-error="handleError"
    @form-changed="onFormChanged"
    @field-blur="onFieldBlur"
  />
  <div class="form-holder q-ma-lg q-pl-xl q-pr-xl" style="max-width: 700px">
    <div class="row">
      <div class='col-12 q-mb-md'>
        <q-input filled dense label="Adjusted Weight" readonly v-model="adjustedWeight" />
      </div>
      <div class='col-12'>
        <q-input filled dense label="Charge" readonly v-model="ticketCharge" />
      </div>
    </div>
  </div>
</template>

<script>
import BaseForm from './BaseForm.vue';
import axios from 'axios';
import { selectedYear } from '../store/store';

export default {
  components: {
    BaseForm
  },
  setup() {
    const restrictDates = (date) => {
      let year = selectedYear.value;
      const start = `${year}/01/01`;
      const end = `${year}/12/31`;
      return date >= start && date <= end;
    };

    return {
      selectedYear,
      restrictDates
    };
  },
  props: {
    ticketId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      ticketHaulData: {
        id: '',
        ticket_number: '',
        dairy_ticket_number: '',
        date: '',
        customer_id: '',
        feedmill_id: '',
        driver_id: '',
        feed_id: '',
        gross_weight: '',
        tare_weight: '',
        rate: ''
      },
      fields: [
        { model: 'ticket_number', label: 'Ticket Number', required: true, type: 'text', refKey: 'ticketNumberRef' },
        { model: 'dairy_ticket_number', label: 'Dairy Ticket Number', required: false, type: 'text' },
        { model: 'date', label: 'Date', required: true, type: 'datetime', openDatePickerOnFocus: true },
        { model: 'customer_id', label: 'Customer', required: true, type: 'select', optionsUrl: '/api/v1/customer/customers/', labelField: 'name' },
        { model: 'feedmill_id', label: 'Feedmill', required: true, type: 'select', optionsUrl: '/api/v1/feedmill/feedmills/', labelField: 'name' },
        { model: 'driver_id', label: 'Driver', required: true, type: 'select', optionsUrl: '/api/v1/driver/drivers/', labelField: 'name' },
        { model: 'feed_id', label: 'Feed', required: true, type: 'select', optionsUrl: '/api/v1/feed/feeds/', labelField: 'name' },
        { model: 'gross_weight', label: 'Gross Weight (lbs)', required: true, type: 'number' },
        { model: 'tare_weight', label: 'Tare Weight (lbs)', required: true, type: 'number' },
        { model: 'rate', label: 'Rate ($ per ton)', required: true, type: 'number' }
      ],
      // Cannot see data from BaseForm so keep local copy of these variables
      current_feedmill_id: null,
      local_gross: 0,
      local_tare: 0,
      local_rate: 0,
      recentTicketNumbers: [],
      duplicateTicketWarningShownFor: null,
    };
  },
  computed: {
    formTitle() {
      return this.ticketId ? `Edit Ticket Haul` : 'Add Ticket Haul';
    },
    submitUrl() {
      return this.ticketId ? `/api/v1/ticket_haul/ticket-hauls/${this.ticketId}/` : '/api/v1/ticket_haul/ticket-hauls/';
    },
    method() {
      return this.ticketId ? 'put' : 'post';
    },
    adjustedWeight() {
      let net_weight = this.local_gross - this.local_tare
      return (net_weight / 2000).toFixed(2)
    },
    ticketCharge() {
      return (this.adjustedWeight * this.local_rate).toFixed(2)
    },
  },
  mounted() {
    this.loadRecentTickets();
    if (this.ticketId) {
      this.loadTicketHaulData();
    }
  },
  methods: {
    loadRecentTickets() {
      axios.get('/api/v1/ticket_haul/ticket-hauls/recent-numbers/')
        .then(response => {
          this.recentTicketNumbers = response.data;
        })
        .catch(console.error);
    },
    loadTicketHaulData() {
      axios.get(`/api/v1/ticket_haul/ticket-hauls/${this.ticketId}/`).then(response => {
        this.ticketHaulData = { 
          ...response.data, 
          dairy_ticket_number: response.data.dairy_ticket_number || '',
          customer_id: response.data.customer_id.id,
          feedmill_id: response.data.feedmill_id.id,
          driver_id: response.data.driver_id.id,
          feed_id: response.data.feed_id.id 
        };

        // Ensure the year is correctly set
        const ticketYear = new Date(this.ticketHaulData.date).getFullYear();
        if (ticketYear !== this.selectedYear) {
          this.selectedYear = ticketYear;
        }
      }).catch(console.error);
    },
    fetchFeedmillRate(id) {
      if (!id) {
        return
      }
      axios.get(`/api/v1/feedmill/feedmills/${id}/`)
        .then(response => {
          if (response.data && response.data.rate) {
            let newRate = 0;
            if (response.data.rate === null) {
              newRate = 0;
            } else {
              newRate = +response.data.rate;
            }
            this.ticketHaulData.rate = newRate;
            this.local_rate = newRate;
          }
        })
        .catch(console.error)
    },
    onFormChanged(newVal) {
      if (newVal.ticket_number !== this.ticketHaulData.ticket_number) {
        this.ticketHaulData = { ...this.ticketHaulData, ticket_number: newVal.ticket_number };
      }

      if (newVal.feedmill_id !== this.current_feedmill_id) {
        this.ticketHaulData = { ...this.ticketHaulData, ...newVal };
        this.ticketHaulData.feedmill_id = newVal.feedmill_id
        this.current_feedmill_id = newVal.feedmill_id;
        this.fetchFeedmillRate(this.current_feedmill_id)
      }

      if (newVal.gross_weight !== this.local_gross) {
        if (newVal.gross_weight === '') {
          this.local_gross = 0; 
        } else {
          this.local_gross = newVal.gross_weight;
        }
      }

      //update local tare
      if (newVal.tare_weight !== this.local_tare) {
        if (newVal.tare_weight === '') {
          this.local_tare = 0; 
        } else {
          this.local_tare = newVal.tare_weight;
        }
      }
      //for some reason driver resetting on new ticket
      if (newVal.driver_id !== this.ticketHaulData.driver_id) {
        this.ticketHaulData = { ...this.ticketHaulData, ...newVal };
        this.ticketHaulData.driver_id = newVal.driver_id
      }

      if (newVal.feed_id !== this.ticketHaulData.feed_id) {
        this.ticketHaulData = { ...this.ticketHaulData, ...newVal };
        this.ticketHaulData.feed_id = newVal.feed_id
      }
    },
    onFieldBlur(field, value) {
      if (field.model !== 'ticket_number') {
        return;
      }

      this.checkDuplicateTicketNumber(value);
    },
    checkDuplicateTicketNumber(ticketNumber = this.ticketHaulData.ticket_number) {
      if (!ticketNumber || !this.recentTicketNumbers.includes(ticketNumber)) {
        this.duplicateTicketWarningShownFor = null;
        return;
      }

      if (this.duplicateTicketWarningShownFor === ticketNumber) {
        return;
      }

      this.duplicateTicketWarningShownFor = ticketNumber;
      window.alert(`Duplicate Ticket Number\n\nTicket number ${ticketNumber} has been recently entered.`);
    },
    handleSuccess() {
      this.$q.notify({
        color: 'green',
        position: 'top',
        message: 'Ticket Haul saved successfully!',
        icon: 'check_circle',
        timeout: 3000
      });
      if (this.method === 'post') {
        this.ticketHaulData.ticket_number = '';
        this.ticketHaulData.dairy_ticket_number = '';
        this.ticketHaulData.gross_weight = '';
        this.ticketHaulData.tare_weight = '';
        this.duplicateTicketWarningShownFor = null;

        this.local_gross = 0;
        this.local_tare = 0; 

        this.$refs.baseForm.refreshLocalFormData();
        this.$refs.baseForm.focusField('ticketNumberRef');
      }
    },
    handleError() {
      this.$q.notify({
        color: 'red',
        position: 'top',
        message: 'There was a problem saving the ticket haul!',
        icon: 'error',
        timeout: 3000
      });
    }
  }
};
</script>
